import { createServerFn } from "@tanstack/react-start";
import {
  readLatestFishingReport,
  readLatestPredictions,
  readLatestSnapshotPayload,
  readSnapshotStatuses,
  readTodayFishability,
} from "./conditions-read.server";
import type { BarometerPayload } from "./marine-feeds.server";
import { marinePayloadToFahrenheit } from "./marine-units";

import {
  SOURCE_NOAA_TIDES,
  SOURCE_NOAA_TIDE_HEIGHTS,
  SOURCE_OPEN_METEO_FORECAST,
  SOURCE_OPEN_METEO_MARINE,
} from "./conditions-sources.server";
import {
  BAROMETER_STALE_SECONDS,
  MARINE_TTL_MS,
  TIDES_TTL_MS,
  WEATHER_TTL_MS,
  BAROMETER_TTL_MS,
  setEdgeCache,
} from "./marine-upstream.server";

// This module is intentionally thin: only imports, types, and server-function
// declarations. Every handler now reads exclusively from the
// `conditions_snapshots` / `fishing_reports` tables — no upstream API is ever
// contacted on a page render. The scheduled `fetch-conditions` job owns all
// outbound requests.

export type { BarometerPayload };

export const getMarineWeatherFn = createServerFn({ method: "GET" }).handler(
  async () => {
    setEdgeCache(WEATHER_TTL_MS / 1000);
    return readLatestSnapshotPayload<any>(SOURCE_OPEN_METEO_FORECAST);
  },
);

export const getMarineConditionsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    setEdgeCache(MARINE_TTL_MS / 1000);
    // The snapshot stores sea surface temperature in Celsius for the scoring
    // engine; every display surface labels it °F, so normalise on read.
    return marinePayloadToFahrenheit(
      await readLatestSnapshotPayload<any>(SOURCE_OPEN_METEO_MARINE),
    );
  },
);


export const getTidesFn = createServerFn({ method: "GET" }).handler(async () => {
  setEdgeCache(TIDES_TTL_MS / 1000);
  return readLatestPredictions<any>(SOURCE_NOAA_TIDES);
});

export const getTideHeightsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    setEdgeCache(TIDES_TTL_MS / 1000);
    return readLatestPredictions<any>(SOURCE_NOAA_TIDE_HEIGHTS);
  },
);

// Barometer readings come out of the same stored forecast snapshot.
export const getBarometerFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<BarometerPayload | null> => {
    setEdgeCache(BAROMETER_TTL_MS / 1000, BAROMETER_STALE_SECONDS);
    const payload = await readLatestSnapshotPayload<{
      current: { time: string; pressure_msl: number; surface_pressure?: number };
      hourly: { time: string[]; pressure_msl: number[] };
    }>(SOURCE_OPEN_METEO_FORECAST);
    if (!payload) return null;
    return {
      fetchedAt: Date.now(),
      current: payload.current,
      hourly: payload.hourly,
    };
  },
);

/** Latest stored narrative plus per-source freshness for the report page. */
export const getFishingReportFn = createServerFn({ method: "GET" }).handler(
  async () => {
    setEdgeCache(WEATHER_TTL_MS / 1000);
    const [report, sources] = await Promise.all([
      readLatestFishingReport(),
      readSnapshotStatuses(),
    ]);
    const lastUpdated = sources
      .map((s) => s.fetched_at)
      .filter((v): v is string => Boolean(v))
      .sort()
      .pop();
    return { report, sources, lastUpdated: lastUpdated ?? null };
  },
);

/** Today's headline fishability score for the TV signage board. */
export const getTodayFishabilityFn = createServerFn({ method: "GET" }).handler(
  async () => {
    setEdgeCache(WEATHER_TTL_MS / 1000);
    return await readTodayFishability();
  },
);
